import { useEffect, useState } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { assistedVote, listAspirants, type Aspirant, type Nomination } from './api'

/**
 * Casting on behalf of a member who cannot vote themselves.
 *
 * ## Why this exists at all
 *
 * Phone coverage on a voter register is never complete, and the members without a number on
 * record are disproportionately rural, elderly and poor. Without this route they are simply
 * disenfranchised — so an official verifies a physical ID and casts for them.
 *
 * ## The official is on the record, the ballot is not
 *
 * `assisted_by` is written to the roll row, so who vouched is answerable afterwards. The
 * BALLOT still carries nothing — no voter, no official — which is why an assisted vote is
 * exactly as secret as any other. The screen says so, because an official who believes the
 * system records how each member voted will be asked to prove it by somebody.
 *
 * ## The receipt belongs to the member
 *
 * It is shown once and cannot be recovered. The official has to hand it over, and the copy
 * instruction says that plainly — a receipt left on the officialʼs screen is a receipt the
 * member never got.
 */
export function AssistedVoteModal({ open, contest, onClose, onDone }: {
  open: boolean
  contest: Nomination | null
  onClose: () => void
  onDone: () => void | Promise<void>
}) {
  const [ballot, setBallot] = useState<Aspirant[] | null>(null)
  const [nationalId, setNationalId] = useState('')
  const [aspirantId, setAspirantId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setBallot(null); setNationalId(''); setAspirantId(null)
      setError(null); setBusy(false); setReceipt(null)
      return
    }
    if (!contest) return
    void (async () => {
      try {
        setError(null)
        const all = await listAspirants(contest.id)
        // Only CLEARED aspirants are on a ballot — the same rule the server enforces when
        // the vote is cast, surfaced here so an official cannot pick a name that will be
        // refused a second later.
        setBallot(all.filter((a) => a.statusCode === 'CLEARED'))
      } catch (err) {
        setError(explain(err))
        setBallot([])
      }
    })()
  }, [open, contest])

  const submit = async () => {
    if (!contest || !aspirantId || !nationalId.trim()) return
    setBusy(true)
    try {
      setError(null)
      const result = await assistedVote(contest.id, {
        nationalId: nationalId.trim(), aspirantId,
      })
      setReceipt(result.receipt)
      await onDone()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const chosen = ballot?.find((a) => a.id === aspirantId)

  return (
    <Modal open={open} onClose={onClose} width="md"
      title={receipt ? 'Vote recorded' : 'Record a vote for a member'}>
      {receipt ? (
        <div className="space-y-4 text-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
          <p className="text-erp-sm">
            The vote has been recorded for <strong>{contest?.title}</strong>.
          </p>
          <div className="rounded-lg border border-border px-4 py-4">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              The member's receipt
            </span>
            <span className="block font-mono text-erp-base tracking-wider break-all">
              {receipt}
            </span>
          </div>
          <Note tone="bad">
            Write this down and give it to the member now. It is shown once and cannot be
            recovered — it is how they confirm their vote was counted, and it does not reveal
            who they voted for.
          </Note>
          <button type="button" onClick={onClose} className="erp-btn h-10 w-full text-erp-sm">
            Done
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <ErrorStrip message={error} className="rounded-md border" />

          <Note>
            Only for a member who cannot vote themselves — no phone on record, or voting in
            person. Check their physical ID first: your account is recorded against this vote,
            though the ballot itself stays anonymous.
          </Note>

          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Member's ID number
            </span>
            <input value={nationalId} onChange={(e) => setNationalId(e.target.value)}
              inputMode="numeric" maxLength={64} autoFocus
              className="erp-input h-10 w-full text-erp-sm"
              placeholder="As printed on the document" />
          </label>

          <div>
            <span className="block text-erp-xs text-muted-foreground mb-1">Their choice</span>
            {ballot === null ? (
              <p className="text-erp-xs text-muted-foreground py-2">
                <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Loading the ballot
              </p>
            ) : ballot.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">
                No aspirants have been cleared for this contest.
              </p>
            ) : (
              <div className="space-y-1.5">
                {ballot.map((a) => (
                  <button key={a.id} type="button" onClick={() => setAspirantId(a.id)}
                    className={cn(
                      'w-full rounded-md border px-3 py-2.5 text-left flex items-center gap-3',
                      aspirantId === a.id
                        ? 'border-chocolate bg-chocolate-soft/30'
                        : 'border-border hover:border-chocolate/50')}>
                    <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground
                                     inline-flex items-center justify-center text-erp-xs shrink-0">
                      {a.ballotPosition ?? '•'}
                    </span>
                    <span className="text-erp-sm font-medium">{a.ballotName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {chosen && (
            <Note tone="bad">
              You are about to cast a vote for <strong>{chosen.ballotName}</strong> on this
              member's behalf. It cannot be changed or withdrawn afterwards.
            </Note>
          )}

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
              Cancel
            </button>
            <button type="button"
              disabled={busy || !aspirantId || !nationalId.trim()}
              onClick={() => void submit()}
              className={cn('erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2',
                (busy || !aspirantId || !nationalId.trim()) && 'opacity-50 cursor-not-allowed')}>
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Cast this vote
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
